// "Covered" marks on a meeting's agenda (kanban 108573ba): the chair ticks an
// item off as the room finishes with it, so a shared screen shows where the
// meeting is. The marks are per meeting (series + day) and live in the
// browser only; nothing here is a roadmap fact. Storage is wrapped so a
// private window or blocked site data degrades to an in-memory set.

import type { SeriesId } from './meetingSeries';

const PREFIX = 'roadmap.meeting.covered.';

export function coveredStorageKey(series: SeriesId, meetingDayIso: string): string {
  return `${PREFIX}${series}.${meetingDayIso}`;
}

export function readCovered(series: SeriesId, meetingDayIso: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(coveredStorageKey(series, meetingDayIso));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []);
  } catch {
    return new Set();
  }
}

export function writeCovered(series: SeriesId, meetingDayIso: string, covered: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(coveredStorageKey(series, meetingDayIso), JSON.stringify([...covered]));
  } catch {
    // Storage unavailable: the marks live for this page only.
  }
}

/** Toggle one item; returns the new set (never mutates the one given). */
export function toggleCovered(covered: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(covered);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** The index to move to after covering item `at`: the next uncovered item
 * after it, else the first uncovered before it, else stay. */
export function nextUncovered(keys: readonly string[], covered: ReadonlySet<string>, at: number): number {
  for (let i = at + 1; i < keys.length; i++) if (!covered.has(keys[i])) return i;
  for (let i = 0; i < at; i++) if (!covered.has(keys[i])) return i;
  return at;
}
